// tenant/InitialConfirmationPage.tsx
import React, { CSSProperties, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import TenantSignupLayout from '../TenantSignupLayout';
import moment from 'moment';

type LocationState = {
  dateSelected: string;
  timeSelected: string;
  address: string;
  type: string; // e.g. "PICKUP"
};

const baseFont: CSSProperties = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  color: '#222',
  fontSize: 14,
};

const sectionCard: CSSProperties = {
  backgroundColor: '#3A1F52',
  borderRadius: 12,
  padding: 20,
  color: '#fff',
  marginBottom: 24,
};

const row: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 10,
};

const labelStyle: CSSProperties = {
  ...baseFont,
  fontSize: 13,
  color: '#D0C9E9',
};

const valueStyle: CSSProperties = {
  ...baseFont,
  color: '#fff',
};

const primaryButton: CSSProperties = {
  ...baseFont,
  marginTop: 24,
  width: '100%',
  padding: '10px 16px',
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#7B1FA2',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'center' as const,
};

const ghostButton: CSSProperties = {
  ...baseFont,
  marginTop: 12,
  width: '100%',
  padding: '10px 16px',
  borderRadius: 6,
  border: '1px solid #7B1FA2',
  backgroundColor: 'transparent',
  color: '#7B1FA2',
  fontWeight: 500,
  cursor: 'pointer',
  textAlign: 'center' as const,
};

async function sendOrderEmail() {
  try {
    const apiKey = process.env.REACT_APP_SEND_EMAIL_API_KEY;

    await fetch(
      'https://yrigzkw0s9.execute-api.us-east-2.amazonaws.com/alpha/sendEmailFunction',
      {
        method: 'POST',
        headers: apiKey ? { 'X-API-Key': apiKey } : {},
      }
    );
  } catch (err) {
    console.error('Error sending order email', err);
  }
}

const InitialConfirmationPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const state = location.state;

  useEffect(() => {
    console.log('[InitialConfirmationPage] route state:', state);
    if (state) {
      sendOrderEmail();
    }
  }, [state]);

  // If someone opens this page directly
  if (!state) {
    return (
      <TenantSignupLayout
        title="Almost there"
        subtitle="We weren’t able to find your order details."
        step={7}
        totalSteps={7}
      >
        <div style={baseFont}>
          <p style={{ marginBottom: 16 }}>
            It looks like you landed here without completing the signup flow first.
          </p>
          <button
            type="button"
            onClick={() => history.push('/signup')}
            style={primaryButton}
          >
            Start over
          </button>
        </div>
      </TenantSignupLayout>
    );
  }

  const { dateSelected, timeSelected, address, type } = state;
  const formattedDate = moment(dateSelected).format('MMMM DD, YYYY');
  const prettyType =
    type === 'PICKUP'
      ? 'Initial pickup'
      : type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();

  return (
    <TenantSignupLayout
      title="Your order has been requested!"
      subtitle="We’ll confirm your pickup time and send you a reminder."
      step={7}
      totalSteps={7}
    >
      <div style={baseFont}>
        {/* Summary card */}
        <div style={sectionCard}>
          <h3
            style={{
              marginTop: 0,
              marginBottom: 12,
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            Order details
          </h3>

          <div style={row}>
            <span style={labelStyle}>Date</span>
            <span style={valueStyle}>{formattedDate}</span>
          </div>

          <div style={row}>
            <span style={labelStyle}>Time</span>
            <span style={valueStyle}>{timeSelected}</span>
          </div>

          <div style={row}>
            <span style={labelStyle}>Address</span>
            <span style={{ ...valueStyle, textAlign: 'right', maxWidth: 260 }}>
              {address}
            </span>
          </div>

          <div style={row}>
            <span style={labelStyle}>Order type</span>
            <span style={valueStyle}>{prettyType}</span>
          </div>
        </div>

        <p
          style={{
            ...baseFont,
            color: '#D0C9E9',
            fontSize: 13,
            marginBottom: 4,
          }}
        >
          You’ll receive a confirmation email shortly with your appointment details.
        </p>
        <p
          style={{
            ...baseFont,
            color: '#D0C9E9',
            fontSize: 13,
          }}
        >
          Once your items are stored, you’ll be able to see your bins, request returns,
          and manage your account from your tenant portal.
        </p>

        <button
          type="button"
          onClick={() => history.push('/tenant/home')}
          style={primaryButton}
        >
          Go to my tenant portal
        </button>

        <button
          type="button"
          onClick={() => history.push('/')}
          style={ghostButton}
        >
          Back to bins-storage.com
        </button>
      </div>
    </TenantSignupLayout>
  );
};

export default InitialConfirmationPage;
